import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import type { ColumnDef } from "@tanstack/react-table";
import useSWR from "swr";
import { SlideOut } from "../../SlideOut/SlideOut";
import { DownloadIcon, LinkIcon } from "../../Icons/Icons";
import { TablePlaceholder, useStoreState } from "../../../util/util";
import type { DocumentCollectionValue } from "../../../util/collectionAttributeDisplayValue";
import { Table } from "../../Table/Table";
import { TableCellTwoLine } from "../../Table/TableCellTwoLine";
import {
  GetDocumentIcon,
  get_content_type,
} from "../../../pages/admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import type {
  Assets,
  GridItemProps,
  SupportedAssetType,
} from "../../../types/types.PIM";
import { GhostButtonMediumV2 } from "../../Buttons/Buttons";
import { endpoints } from "../../../endpoints";
import { useAuthContext } from "../../Auth";
import { DocumentPreview } from "../../../pages/admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/DocumentPreview";

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  justify-content: flex-end;
`;

export const DocumentMoreModal = ({
  docs,
  attributeName,
  onClose,
}: {
  docs: DocumentCollectionValue[];
  attributeName: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { roleIsGuest, roleIsSomeKindOfBuyer } = useAuthContext();
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  const { data: previewAsset } = useSWR<Assets>(
    selectedAssetId
      ? endpoints.v2_tenants_id_pim_assets_id(tenant_id, selectedAssetId)
      : null,
    { revalidateOnFocus: false }
  );

  const assetForPreview: (Assets & GridItemProps) | null = previewAsset
    ? {
        ...previewAsset,
        can_edit: false,
        can_change_visibility: false,
        can_delete: false,
        can_external_download: roleIsGuest
          ? previewAsset.visibility === "public"
          : !roleIsSomeKindOfBuyer || previewAsset.visibility !== "internal",
        can_view_details: true,
      }
    : null;

  const handleNameClick = (doc: DocumentCollectionValue) => {
    if (doc.is_external && doc.url) {
      window.open(doc.url, "_blank", "noopener noreferrer");
    } else {
      setSelectedAssetId(doc.asset_id);
    }
  };

  const columns = useMemo(
    (): ColumnDef<DocumentCollectionValue>[] => [
      {
        id: "display_name",
        header: t("Asset Name"),
        cell: ({ row }) => {
          const doc = row.original;
          const source = doc.host_name ?? t("Agilis");
          return (
            <NameCell
              onClick={() => handleNameClick(doc)}
              style={{ cursor: "pointer" }}
            >
              <div>
                {doc.is_external ? (
                  <LinkIcon width={20} height={20} />
                ) : (
                  <GetDocumentIcon
                    asset_type={
                      doc.asset_type
                        ? (doc.asset_type as SupportedAssetType)
                        : "document"
                    }
                    file_type={get_content_type(
                      doc.content_type ?? "application/pdf"
                    )}
                  />
                )}
              </div>
              <TableCellTwoLine
                primaryText={doc.display_name || t("Document")}
                secondaryText={source}
              />
            </NameCell>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const doc = row.original;
          const handleClick = () => {
            window.open(doc.url, "_blank", "noopener noreferrer");
          };
          return (
            <ActionsCell>
              <GhostButtonMediumV2
                type="button"
                onClick={handleClick}
                datatip={t("Open")}
                datafor={`open-doc-${doc.asset_id}`}
              >
                <DownloadIcon width={16} height={16} />
              </GhostButtonMediumV2>
            </ActionsCell>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  return (
    <>
      <SlideOut title={attributeName} show closeFlyout={onClose}>
        <Table
          columns={columns}
          data={docs}
          isLoading={false}
          error={undefined}
          disableTableSelection
          lastChildleftAlign
          Placeholder={<TablePlaceholder message={t("No items to show.")} />}
        />
      </SlideOut>
      {assetForPreview && (
        <DocumentPreview
          assets={[assetForPreview]}
          selected_asset_idx={0}
          onCancelPreview={() => setSelectedAssetId(null)}
        />
      )}
    </>
  );
};
